// ============================================================
// TaskFlow OS - Header Bar
// ============================================================

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import type { Notification } from '../../lib/types';
import { IconBell, IconSearch } from '../../icons/SvgIcons';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, actions }) => {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) setNotifications(data as Notification[]);
    };

    fetchNotifications();

    // Realtime subscription
    const channel = supabase
      .channel(`header-notifications-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifications((prev) => [payload.new as Notification, ...prev]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const markAsRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true, read_at: new Date().toISOString() }).eq('id', id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
  };

  return (
    <header className="glass-header sticky top-0 z-30 px-6 h-16 flex items-center justify-between gap-4">
      {/* Left: Title */}
      <div className="flex-1 min-w-0">
        {title && <h2 className="text-lg font-bold text-white truncate">{title}</h2>}
        {subtitle && <p className="text-xs text-white/40 truncate">{subtitle}</p>}
      </div>

      {/* Center: Search */}
      <div className="hidden md:flex items-center max-w-md flex-1">
        <div className="relative w-full">
          <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="検索..."
            className="glass-input pl-9 py-2 text-sm"
          />
        </div>
      </div>

      {/* Right: Actions + Notifications */}
      <div className="flex items-center gap-3">
        {actions}

        {/* Notification bell */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-xl hover:bg-white/5 transition-colors"
          >
            <IconBell size={20} className="text-white/60" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-[9px] text-white font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification dropdown */}
          {showNotifications && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
              <div className="absolute right-0 top-full mt-2 w-80 glass-panel rounded-xl p-0 z-50 max-h-[400px] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5">
                  <h3 className="text-sm font-semibold text-white">通知</h3>
                </div>
                <div className="overflow-y-auto max-h-[340px]">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-xs text-white/30">通知はありません</div>
                  ) : (
                    notifications.map((notif) => (
                      <button
                        key={notif.id}
                        onClick={() => markAsRead(notif.id)}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${!notif.is_read ? 'bg-emerald-500/5' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white truncate">{notif.title}</p>
                            {notif.content && <p className="text-[10px] text-white/40 mt-0.5 line-clamp-2">{notif.content}</p>}
                            <p className="text-[10px] text-white/20 mt-1">
                              {new Date(notif.created_at).toLocaleString('ja-JP')}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
